import type { TOC } from '@ember/component/template-only';
import type { ComponentLike } from '@glint/template';

// ---------------------------------------------------------------------------
// EmptyState – placeholder for empty lists or data
// ---------------------------------------------------------------------------

/**
 * Color theme options for the empty state icon.
 *
 * Each value maps to a TIO semantic-token recipe via the
 * `.app-empty-state--<color>` CSS modifier — see
 * `addons/tio-ui/src/styles/`. The decorative names are
 * kept for API back-compat with the existing 30+ consumers; under
 * the hood `blue` and `teal` collapse onto the same `--info` recipe
 * (the canonical token system has one info family).
 */
export type EmptyStateColor = 'gray' | 'violet' | 'blue' | 'teal' | 'amber' | 'emerald';

interface EmptyStateSignature {
  Args: {
    /** Phosphor icon component to display */
    icon: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
    /** Title text for the empty state */
    title: string;
    /** Description text explaining why the area is empty */
    description?: string;
    /** Color theme for the icon (default: 'gray') */
    color?: EmptyStateColor;
    /** Whether to use compact sizing */
    compact?: boolean;
  };
  Blocks: {
    /** Optional action button or link */
    action?: [];
    /** Optional extra content below the description */
    extra?: [];
  };
  Element: HTMLDivElement;
}

/**
 * EmptyState - A placeholder for empty lists or data sections
 *
 * Displays a centered icon, title, description, and optional action when
 * there is no data to show. Useful for empty search results, unstarted
 * features, or pristine states.
 *
 * Built on the `.app-empty-state*` semantic class family — the color
 * modifier on the root sets `--empty-bg` / `--empty-fg` from the
 * canonical `--tag-*-bg` + `--*-icon` token recipes, and
 * `.app-empty-state-icon-badge` composes against them.
 *
 * @example Basic usage
 * ```gts
 * import EmptyState from 'tio-ui/components/empty-state';
 *
 * <EmptyState
 *   @icon={{PhMagnifyingGlass}}
 *   @title="No results found"
 *   @description="Try adjusting your search or filters"
 * />
 * ```
 *
 * @example With action button
 * ```gts
 * <EmptyState @icon={{PhPlus}} @title="No applications yet" @description="Start your first application" @color="violet">
 *   <:action>
 *     <Button @intent="primary">Get Started</Button>
 *   </:action>
 * </EmptyState>
 * ```
 */
const EmptyState: TOC<EmptyStateSignature> = <template>
  <div
    data-test-empty-state
    class="app-empty-state app-empty-state--{{if @color @color 'gray'}}
      {{if @compact 'app-empty-state--compact' 'app-empty-state--regular'}}"
    ...attributes
  >
    <div data-test-empty-state-icon class="app-empty-state-icon-badge">
      {{#let @icon as |Icon|}}
        <Icon @size={{if @compact 24 32}} class="app-empty-state-icon" />
      {{/let}}
    </div>
    <h3 data-test-empty-state-title class="app-empty-state-title">
      {{@title}}
    </h3>
    {{#if @description}}
      <p
        data-test-empty-state-description
        class="app-empty-state-description {{if @compact 'app-empty-state-description--compact'}}"
      >
        {{@description}}
      </p>
    {{/if}}
    {{#if (has-block "action")}}
      <div data-test-empty-state-action class="app-empty-state-action">
        {{yield to="action"}}
      </div>
    {{/if}}
    {{#if (has-block "extra")}}
      <div class="app-empty-state-extra">
        {{yield to="extra"}}
      </div>
    {{/if}}
  </div>
</template>;

export default EmptyState;

export type { EmptyStateSignature };
